<template>
  <div v-if="toast.show" class="fixed top-4 right-4 z-[1000] max-w-sm">
    <div
      class="px-4 py-2 rounded-lg shadow-lg text-white text-sm font-medium transition-all"
      :class="toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'"
    >
      {{ toast.message }}
    </div>
  </div>
  <div v-if="isOpen" class="fixed inset-0 z-[200] flex items-center justify-center px-4 overflow-y-auto bg-black/50 backdrop-blur-sm" @click.self="closeModal">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-8 transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-visible max-h-[80vh] flex flex-col">
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
        <h2 class="text-lg font-bold m-0">Material Selection</h2>
        <button @click="closeModal" class="p-1 hover:bg-white/10 rounded-full transition-colors text-white">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <div class="p-6 flex-1 overflow-y-auto">
        <div class="flex justify-between items-center gap-3 mb-4">
          <select
            v-model="selectedIssuingToKey"
            class="w-[240px] px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
          >
            <option disabled :value="null">Select Department/Category</option>
            <option v-for="opt in issuingToOptions" :key="opt.key" :value="opt.key">
              {{ opt.name }}
            </option>
          </select>
          <input
            v-model="searchQuery"
            type="text"
            class="w-[300px] px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white"
            placeholder="Search by item code or name..."
          >
        </div>

        <table class="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
          <thead>
            <tr>
              <th>No.</th>
              <th>Item Code</th>
              <th>Item Name</th>
              <th>Category</th>
              <th>U.O.M.Stock</th>
              <th>U.O.M.Production</th>
              <th>Material</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in filteredItems" :key="item.id" class="hover:bg-slate-100/50 transition-colors border-b">
              <td>{{ index + 1 }}</td>
              <td>{{ item.itemCode }}</td>
              <td>{{ item.itemName }}</td>
              <td>{{ item.category?.name }}</td>
              <td>{{ item.unit }}</td>
              <td>{{ item.unitProduction || '---' }}</td>
              <td class="text-center">
                <input
                  v-if="selectedIssuingToKey !== null"
                  type="checkbox"
                  :checked="selectedSet.has(item.id)"
                  @change="toggleItemSelection(item)"
                  class="w-4 h-4 rounded border-slate-300 text-[#0d4169] focus:ring-[#0d4169] cursor-pointer"
                >
              </td>
            </tr>
            <tr v-if="filteredItems.length === 0">
              <td colspan="7" style="text-align:center; color:#94a3b8; padding: 48px 15px; font-style: italic;">
                No items found.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-between items-center">
        <div class="text-sm text-slate-600">
          {{ selectedItems.length }} item{{ selectedItems.length !== 1 ? 's' : '' }} selected
        </div>
        <div class="flex gap-3">
          <button @click="closeModal" class="px-6 py-2 bg-slate-500 text-white text-sm font-medium rounded-md hover:bg-slate-600 transition-all">
            Cancel
          </button>
          <button
            @click="confirmSelection"
            :disabled="isConfirming"
            class="min-w-[100px] px-6 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
          >
            <div
              v-if="isConfirming"
              class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
            ></div>
            <span v-if="!isConfirming">Confirm Selection</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, defineProps, defineEmits } from 'vue'
import store from '../../../store'
import { apiFetch } from '../../../utils/api'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  ingredient: {
    type: Object,
    default: () => ({})
  },
  itemsList: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['close', 'confirm'])
const selectedItems = ref([])
const initialSelectedItems = ref(new Set())
const searchQuery = ref('')
const departments = ref([])
const incomeCategories = ref([])
const selectedIssuingToKey = ref(null)
const toast = ref({ show: false, message: '', type: 'success' })
const isConfirming = ref(false)

const selectedSet = computed(() => new Set(selectedItems.value))

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || currentUser.value?.branch?.id || null)

const filteredItems = computed(() => {
  const source = Array.isArray(props.itemsList) ? [...props.itemsList] : []
  source.sort((a, b) => {
    const nameA = String(a?.itemName || '').toLowerCase()
    const nameB = String(b?.itemName || '').toLowerCase()
    return nameA.localeCompare(nameB)
  })

  const q = String(searchQuery.value || '').toLowerCase().trim()
  if (!q) return source

  return source.filter(item =>
    String(item?.itemCode || '').toLowerCase().includes(q) ||
    String(item?.itemName || '').toLowerCase().includes(q)
  )
})

function parseIssuingToSelection(key) {
  const raw = String(key || '').trim()
  if (!raw) return { departmentId: null, incomeCategoryId: null }
  if (raw.startsWith('department-')) {
    const depId = Number(raw.slice('department-'.length))
    return {
      departmentId: Number.isFinite(depId) ? depId : null,
      incomeCategoryId: null
    }
  }
  const catId = Number(raw)
  return {
    departmentId: null,
    incomeCategoryId: Number.isFinite(catId) ? catId : null
  }
}

function incomeCategoryIsActive(cat) {
  if (!cat || typeof cat !== 'object') return false
  if (cat.enabled != null) return cat.enabled === true || cat.enabled === 1 || cat.enabled === '1'
  if (cat.status != null) return Number(cat.status) === 1
  return true
}

const issuingToOptions = computed(() => {
  const deptOpts = departments.value.map((dep) => ({
    key: `department-${String(dep.id)}`,
    name: String(dep.name || '').trim()
  })).filter((opt) => opt.name)

  const catOpts = incomeCategories.value.map((cat) => ({
    key: String(cat.id),
    name: String(cat.name || '').trim()
  })).filter((opt) => opt.name)

  return [...catOpts, ...deptOpts].sort((a, b) => a.name.localeCompare(b.name))
})

function showToast(message, type = 'success') {
  toast.value = { show: true, message, type }
  setTimeout(() => {
    toast.value.show = false
  }, 2500)
}

async function loadIssuingToOptions() {
  if (!companyId.value && !branchId.value) return
  try {
    const query = branchId.value
      ? `branchId=${branchId.value}`
      : (companyId.value ? `companyId=${companyId.value}` : '')
    const depUrl = query ? `/departments?${query}` : '/departments'
    const [depRes, incomeCatRes] = await Promise.all([
      apiFetch(depUrl),
      apiFetch('/finance/income-category')
    ])

    if (depRes.ok) {
      const depData = await depRes.json().catch(() => [])
      departments.value = (Array.isArray(depData) ? depData : []).filter((d) => (d.status ?? 1) === 1)
      departments.value.sort((a, b) => String(a.name).localeCompare(String(b.name)))
    } else {
      departments.value = []
    }

    if (incomeCatRes.ok) {
      const incomeCatData = await incomeCatRes.json().catch(() => [])
      incomeCategories.value = Array.isArray(incomeCatData)
        ? incomeCatData.filter(incomeCategoryIsActive)
        : []
      incomeCategories.value.sort((a, b) => String(a.name).localeCompare(String(b.name)))
    } else {
      incomeCategories.value = []
    }
  } catch (err) {
    console.error('Error loading departments/income categories:', err)
  }
}

async function loadSavedSelectionsForScope() {
  if (selectedIssuingToKey.value === null) {
    selectedItems.value = []
    initialSelectedItems.value = new Set()
    return
  }

  const { departmentId, incomeCategoryId } = parseIssuingToSelection(selectedIssuingToKey.value)
  const query = departmentId
    ? `departmentId=${encodeURIComponent(departmentId)}`
    : `incomeCategoryId=${encodeURIComponent(incomeCategoryId)}`

  try {
    const res = await apiFetch(`/material-selection?${query}`)
    if (!res.ok) {
      selectedItems.value = []
      initialSelectedItems.value = new Set()
      return
    }
    const data = await res.json().catch(() => ({ itemIds: [] }))
    const ids = Array.isArray(data?.itemIds) ? data.itemIds : []
    selectedItems.value = ids.map((id) => Number(id)).filter((n) => Number.isFinite(n))
    initialSelectedItems.value = new Set(selectedItems.value)
  } catch (err) {
    console.error('Error loading material selections:', err)
    selectedItems.value = []
    initialSelectedItems.value = new Set()
  }
}

function selectionUnchanged() {
  const current = new Set(selectedItems.value)
  const initial = initialSelectedItems.value
  if (current.size !== initial.size) return false
  for (const id of current) {
    if (!initial.has(id)) return false
  }
  return true
}

function closeModal() {
  emit('close')
}

function toggleItemSelection(item) {
  const index = selectedItems.value.indexOf(item.id)
  if (index >= 0) selectedItems.value.splice(index, 1)
  else selectedItems.value.push(item.id)
}

async function confirmSelection() {
  if (selectedIssuingToKey.value === null) {
    showToast('Please select department or income category', 'error')
    return
  }
  if (isConfirming.value) return

  isConfirming.value = true
  try {
    const currentSet = new Set(selectedItems.value)
    const selectedItemObjects = props.itemsList.filter((item) => currentSet.has(item.id))

    if (!selectionUnchanged()) {
      const { departmentId, incomeCategoryId } = parseIssuingToSelection(selectedIssuingToKey.value)
      const payload = {
        departmentId,
        incomeCategoryId,
        itemIds: selectedItems.value.map((id) => Number(id)).filter((n) => Number.isFinite(n))
      }

      const res = await apiFetch('/material-selection/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        showToast(err.message || 'Failed to save material selection', 'error')
        return
      }
    }

    showToast('Material selection saved successfully', 'success')
    emit('confirm', selectedItemObjects)
    closeModal()
  } catch (error) {
    console.error('Error confirming material selection:', error)
    showToast('Failed to save material selection', 'error')
  } finally {
    isConfirming.value = false
  }
}

onMounted(() => {
  loadIssuingToOptions()
  if (props.isOpen) {
    selectedItems.value = []
    initialSelectedItems.value = new Set()
  }
})

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    selectedItems.value = []
    initialSelectedItems.value = new Set()
  }
})

watch(selectedIssuingToKey, () => {
  loadSavedSelectionsForScope()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
}

th, td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

tbody tr:hover {
  background: #f1f5f9;
}
</style>
